class FacetShort extends HTMLSelectElement {
  constructor() {
    super();
    this.initMotionInView();
    this.addEventListener('change', this.onSelectChange.bind(this));
  }

  get options() {
    return this.getElementsByTagName('option');
  }

  get fakeSelectElement() {
    return this.previousElementSibling;
  }

  initMotionInView() {
    FoxTheme.Motion.inView(this, this.calcSelectWidth.bind(this), { margin: '200px 0px 200px 0px' });
  }

  calcSelectWidth() {
    const selectStyle = window.getComputedStyle(this);
    const selectedOptionText = this.options[this.selectedIndex].text;

    const textElement = this.createTextElement(selectedOptionText, selectStyle);
    document.body.appendChild(textElement);
    const selectWidth = textElement.offsetWidth;
    textElement.remove();

    this.style.setProperty('--width', `${selectWidth + 22}px`);
  }

  createTextElement(text, style) {
    const textElement = document.createElement('span');
    textElement.style.fontFamily = style.fontFamily;
    textElement.style.fontSize = style.fontSize;
    textElement.style.fontWeight = style.fontWeight;
    textElement.style.visibility = 'hidden';
    textElement.style.position = 'absolute';
    textElement.innerHTML = text;
    return textElement;
  }

  onSelectChange(event) {
    this.calcSelectWidth();
    this.updateFormParams(event);
  }

  updateFormParams(event) {
    const form = this.getClosestFacetForm() || this.getFirstFacetForm();

    if (form) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      url.searchParams.delete('page');
      form.renderSection(url.toString(), event);
    }
  }

  getClosestFacetForm() {
    return this.closest('form[is="facet-form"]');
  }

  getFirstFacetForm() {
    return document.querySelector('form[is="facet-form"]');
  }
}
customElements.define('facet-short', FacetShort, { extends: 'select' });

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.minRangeInput = this.querySelector('input[type="range"]:first-child');
    this.maxRangeInput = this.querySelector('input[type="range"]:last-child');
    this.minPriceInput = this.querySelector('input[name="filter.v.price.gte"]');
    this.maxPriceInput = this.querySelector('input[name="filter.v.price.lte"]');

    this.minPriceInput.addEventListener('focus', this.minPriceInput.select);
    this.maxPriceInput.addEventListener('focus', this.maxPriceInput.select);
    this.minPriceInput.addEventListener('change', this.handleInputMinChange.bind(this));
    this.maxPriceInput.addEventListener('change', this.handleInputMaxChange.bind(this));

    this.minRangeInput.addEventListener('change', this.handleRangeMinChange.bind(this));
    this.maxRangeInput.addEventListener('change', this.handleRangeMaxChange.bind(this));
    this.minRangeInput.addEventListener('input', this.handleRangeMinInput.bind(this));
    this.maxRangeInput.addEventListener('input', this.handleRangeMaxInput.bind(this));
  }

  handleInputMinChange(event) {
    event.preventDefault();
    event.target.value = Math.max(
      Math.min(parseInt(event.target.value), parseInt(this.maxPriceInput.value || event.target.max) - 1),
      event.target.min
    );
    this.minRangeInput.value = event.target.value;
    this.minRangeInput.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(this.minRangeInput.value) / parseInt(this.minRangeInput.max)) * 100}%`
    );
  }

  handleInputMaxChange(event) {
    event.preventDefault();
    event.target.value = Math.min(
      Math.max(parseInt(event.target.value), parseInt(this.minPriceInput.value || event.target.min) + 1),
      event.target.max
    );
    this.maxRangeInput.value = event.target.value;
    this.maxRangeInput.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(this.maxRangeInput.value) / parseInt(this.maxRangeInput.max)) * 100}%`
    );
  }

  handleRangeMinChange(event) {
    event.stopPropagation();
    this.minPriceInput.value = event.target.value;
    this.minPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMaxChange(event) {
    event.stopPropagation();
    this.maxPriceInput.value = event.target.value;
    this.maxPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMinInput(event) {
    event.target.value = Math.min(
      parseInt(event.target.value),
      parseInt(this.maxPriceInput.value || event.target.max) - 1
    );
    event.target.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.minPriceInput.value = event.target.value;
  }

  handleRangeMaxInput(event) {
    event.target.value = Math.max(
      parseInt(event.target.value),
      parseInt(this.minPriceInput.value || event.target.min) + 1
    );
    event.target.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.maxPriceInput.value = event.target.value;
  }
}
customElements.define('price-range', PriceRange);

class FacetForm extends HTMLFormElement {
  constructor() {
    super();

    this.dirty = false;
    this.cachedMap = new Map();
    this.customBaseCardsCache = new Map();
    this.customBaseCardsPromiseCache = new Map();
    this.customFilteredCardsCache = new Map();
    this.submitDebounceTimer = null;
    this.customFetchController = null;
    this.customProductsPerPage = 24;
    this.isCustomFiltering = false;
    this.onDocumentClickHandler = this.onDocumentClick.bind(this);
    this.pendingFilterIndex = null;
    this.pendingInputType = null;

    this.addEventListener('change', this.onFormChange);
    this.addEventListener('submit', this.onFormSubmit);

    // Custom metafield filters do not always expose server-side counts.
    // Build those counts from the custom products endpoint.
    this.scheduleCustomFilterCountRefresh();
  }

  connectedCallback() {
    this.scheduleCustomFilterCountRefresh();
    document.addEventListener('click', this.onDocumentClickHandler);
    this.prefetchCustomBaseCards();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClickHandler);
  }

  onFormChange(event) {
    this.dirty = true;
    this.setCustomPageInput(1);

    const changedInput = event?.target;
    const changedFilter = changedInput?.closest?.('[data-filter]');
    this.pendingFilterIndex = changedFilter?.dataset?.index || null;
    this.pendingInputType = changedInput?.type || changedInput?.tagName?.toLowerCase() || null;
    const isCheckboxChange =
      changedInput instanceof HTMLInputElement &&
      changedInput.type === 'checkbox' &&
      changedInput.hasAttribute('data-custom-filter-key');

    if (isCheckboxChange) {
      if (this.submitDebounceTimer) {
        clearTimeout(this.submitDebounceTimer);
        this.submitDebounceTimer = null;
      }
      this.submitDebounceTimer = setTimeout(() => {
        this.dispatchEvent(new Event('submit', { cancelable: true }));
      }, 120);
      return;
    }

    if (this.submitDebounceTimer) {
      clearTimeout(this.submitDebounceTimer);
    }
    this.submitDebounceTimer = setTimeout(() => {
      this.dispatchEvent(new Event('submit', { cancelable: true }));
    }, 220);
  }

  onFormSubmit(event) {
    event.preventDefault();
    if (!this.dirty) return;
    if (this.submitDebounceTimer) {
      clearTimeout(this.submitDebounceTimer);
      this.submitDebounceTimer = null;
    }
    this.scrollToCollectionTop();

    if (this.hasActiveCustomFilters()) {
      const pendingUrl = this.buildCustomBrowserUrl({ page: 1 });
      this.updateURLHash(pendingUrl);
      this.renderCustomActiveFilters(pendingUrl);
      this.renderCustomFilteredProducts(event);
      this.dirty = false;
      return;
    }

    const url = this.buildUrl().toString();
    this.renderSection(url, event);
  }

  onDocumentClick(event) {
    if (this !== document.querySelector('form[is="facet-form"]')) return;

    const paginationLink = event.target.closest('[data-custom-pagination-link]');
    if (!paginationLink) return;

    event.preventDefault();

    const nextPage = parseInt(paginationLink.dataset.page || '1', 10);
    this.setCustomPageInput(nextPage);
    const pendingUrl = this.buildCustomBrowserUrl({ page: nextPage });
    this.updateURLHash(pendingUrl);
    this.renderCustomFilteredProducts(event, { page: nextPage, keepCurrentScroll: true });
  }

  getActiveCustomFilters() {
    const selected = this.querySelectorAll(
      'input[type="checkbox"][data-custom-filter-key][data-custom-filter-value]:checked'
    );
    return Array.from(selected)
      .map((input) => ({
        key: input.dataset.customFilterKey || '',
        value: input.dataset.customFilterValue || '',
      }))
      .filter((item) => item.key && item.value);
  }

  applyCustomFiltersFromUrl(url) {
    const targetUrl = url instanceof URL ? url : new URL(url, window.location.origin);
    const selectedValuesByKey = new Map();

    targetUrl.searchParams.forEach((value, key) => {
      if (!key.startsWith('filter.p.m.custom.')) return;
      if (!selectedValuesByKey.has(key)) selectedValuesByKey.set(key, new Set());
      selectedValuesByKey.get(key).add(value);
    });

    const forms = document.querySelectorAll('form[is="facet-form"]');
    forms.forEach((formElement) => {
      const customInputs = formElement.querySelectorAll(
        'input[type="checkbox"][data-custom-filter-key][data-custom-filter-value]'
      );
      customInputs.forEach((input) => {
        const selectedValues = selectedValuesByKey.get(input.name) || new Set();
        const shouldSelect = selectedValues.has(input.value);
        input.checked = shouldSelect;
        if (shouldSelect && input.disabled) {
          input.disabled = false;
        }
      });
    });
  }

  hasActiveCustomFilters() {
    return this.getActiveCustomFilters().length > 0;
  }

  buildCustomApiUrl() {
    return this.buildCustomApiUrlWithOptions({ includeCustomFilters: true });
  }

  buildCustomApiUrlWithOptions({ includeCustomFilters = true } = {}) {
    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set('view', 'custom-filter-products');

    const formParams = new URLSearchParams(new FormData(this));
    formParams.forEach((value, key) => {
      if (key === 'section_id' || key === 'page') return;
      if (!includeCustomFilters && key.startsWith('filter.p.m.custom.')) return;
      if (value === '') return;
      url.searchParams.append(key, value);
    });

    return url.toString();
  }

  getCurrentCustomPage() {
    const formParams = new URLSearchParams(new FormData(this));
    const formPage = parseInt(formParams.get('page') || '', 10);
    if (Number.isFinite(formPage) && formPage > 0) return formPage;

    const urlPage = parseInt(new URL(window.location.href).searchParams.get('page') || '', 10);
    if (Number.isFinite(urlPage) && urlPage > 0) return urlPage;

    return 1;
  }

  setCustomPageInput(page = 1) {
    const normalizedPage = Number.isFinite(Number(page)) && Number(page) > 1 ? String(Math.floor(Number(page))) : '';
    this.querySelectorAll('input[name="page"]').forEach((input) => {
      input.value = normalizedPage;
    });
  }

  getCustomFilterCacheKey(baseRequestUrl, activeCustomFilters) {
    const filterKey = [...activeCustomFilters]
      .sort((a, b) => `${a.key}:${a.value}`.localeCompare(`${b.key}:${b.value}`))
      .map((item) => `${item.key}=${item.value}`)
      .join('&');
    return `${baseRequestUrl}::${filterKey}`;
  }

  buildCustomBrowserUrl({ page } = {}) {
    const url = new URL(window.location.pathname, window.location.origin);
    const formParams = new URLSearchParams(new FormData(this));
    formParams.forEach((value, key) => {
      if (key === 'section_id' || key === 'page') return;
      if (value === '') return;
      url.searchParams.append(key, value);
    });

    const pageNumber = Number.isFinite(Number(page)) ? Number(page) : this.getCurrentCustomPage();
    if (pageNumber > 1) {
      url.searchParams.set('page', String(Math.floor(pageNumber)));
    }

    return url.toString();
  }

  async renderCustomFilteredProducts(event, { page, keepCurrentScroll = false } = {}) {
    this.isCustomFiltering = true;
    if (!keepCurrentScroll) {
      this.beforeRenderSection();
    } else {
      const loadings = document.querySelectorAll('[data-facet-loading]');
      loadings.forEach((loading) => loading.classList.add('btn--loading'));
      this.renderProductGridSkeleton(this.customProductsPerPage);
    }

    try {
      const activeCustomFilters = this.getActiveCustomFilters();
      const currentPage = Math.max(1, parseInt(page || this.getCurrentCustomPage(), 10) || 1);
      const baseRequestUrl = this.buildCustomApiUrlWithOptions({ includeCustomFilters: false });
      const cacheKey = this.getCustomFilterCacheKey(baseRequestUrl, activeCustomFilters);
      let customResult = this.customFilteredCardsCache.get(cacheKey);

      if (!customResult) {
        if (this.customFetchController) {
          this.customFetchController.abort();
        }
        this.customFetchController = new AbortController();
        const baseCards = await this.getCustomBaseCards(baseRequestUrl, this.customFetchController.signal);
        customResult = this.filterCustomProducts(baseCards, activeCustomFilters);
        this.customFilteredCardsCache.set(cacheKey, customResult);
      }

      const totalPages = Math.max(1, Math.ceil(customResult.count / this.customProductsPerPage));
      const clampedPage = Math.min(currentPage, totalPages);
      const paginatedProducts = this.paginateCustomCards(customResult.cards, clampedPage);

      this.renderCustomProductGrid(paginatedProducts, customResult.count);
      this.renderCustomPagination(customResult.count, clampedPage);
      this.updateCustomCount(customResult.count);
      this.setCustomPageInput(clampedPage);

      const browserUrl = this.buildCustomBrowserUrl({ page: clampedPage });
      this.updateURLHash(browserUrl);
      this.updatePaginationVisibility(browserUrl, { forceShow: true });
      this.renderCustomActiveFilters(browserUrl);
      this.scheduleCustomFilterCountRefresh();

      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: document.body.innerHTML });
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Custom filter API error:', error);
    } finally {
      this.customFetchController = null;
      this.afterRenderSection();
    }
  }

  paginateCustomCards(cards, page = 1) {
    const safePage = Math.max(1, parseInt(page, 10) || 1);
    const start = (safePage - 1) * this.customProductsPerPage;
    const end = start + this.customProductsPerPage;
    return cards.slice(start, end);
  }

  renderCustomProductGrid(productsHtmlArray, totalCount) {
    const productsList = document.getElementById('ProductsList');
    if (!productsList) return;

    if (totalCount > 0) {
      productsList.innerHTML = productsHtmlArray.join('');
      return;
    }

    productsList.innerHTML = `
      <div class="f-column" style="position: absolute; left: 50%;">
        <div class="empty-state flex flex-col items-center justify-center gap-6 text-center">
          <div class="gap-3 flex flex-col items-center justify-center text-center">
            <p class="h4">No results found</p>
            <p class="m-0">Try a different filter combination.</p>
            <p class="m-0">Please call <a href="tel:8884749966" target="_blank" title="tel:8884749966" class="tw-underline" style="text-decoration: underline;">(888)-474-9966</a> for more details</p>
          </div>
        </div>
      </div>
    `;
  }

  async getCustomBaseCards(baseRequestUrl, signal) {
    if (this.customBaseCardsCache.has(baseRequestUrl)) {
      return this.customBaseCardsCache.get(baseRequestUrl);
    }

    if (this.customBaseCardsPromiseCache.has(baseRequestUrl)) {
      return this.customBaseCardsPromiseCache.get(baseRequestUrl);
    }

    const pendingRequest = (async () => {
      let requestUrl = baseRequestUrl;
      const cards = [];

      while (requestUrl) {
        const fetchOptions = signal ? { signal } : {};
        const response = await fetch(requestUrl, fetchOptions);
        const data = await response.json();
        console.log('[custom-filter-products] getCustomBaseCards response', {
          requestUrl,
          hasHtml: Boolean(data?.html),
          htmlLength: data?.html?.length || 0,
          paginateUrl: data?.paginateUrl || null,
        });
        const parser = document.createElement('div');
        parser.innerHTML = data?.html || '';

        parser.querySelectorAll('.product-column').forEach((card) => {
          cards.push({
            html: card.outerHTML,
            searchText: String(card.dataset.customSearchText || '').toLowerCase(),
          });
        });

        requestUrl = data?.paginateUrl ? new URL(data.paginateUrl, window.location.origin).toString() : null;
      }

      this.customBaseCardsCache.set(baseRequestUrl, cards);
      return cards;
    })();

    this.customBaseCardsPromiseCache.set(baseRequestUrl, pendingRequest);

    try {
      return await pendingRequest;
    } finally {
      this.customBaseCardsPromiseCache.delete(baseRequestUrl);
    }
  }

  prefetchCustomBaseCards() {
    const hasCustomFilterInputs = this.querySelector('input[type="checkbox"][data-custom-filter-key][data-custom-filter-value]');
    if (!hasCustomFilterInputs) return;

    const baseRequestUrl = this.buildCustomApiUrlWithOptions({ includeCustomFilters: false });
    if (this.customBaseCardsCache.has(baseRequestUrl) || this.customBaseCardsPromiseCache.has(baseRequestUrl)) return;

    this.getCustomBaseCards(baseRequestUrl).catch(() => {});
  }

  filterCustomProducts(baseCards, activeCustomFilters) {
    if (!activeCustomFilters.length) {
      const allCards = baseCards.map((card) => card.html);
      return { cards: allCards, count: allCards.length };
    }

    const filteredCards = baseCards
      .filter((card) => this.matchesCustomSearchText(card.searchText, activeCustomFilters))
      .map((card) => card.html);

    return {
      cards: filteredCards,
      count: filteredCards.length,
    };
  }

  renderCustomPagination(totalCount, currentPage) {
    const paginationEl = document.querySelector('#ProductGridContainer .pagination');
    if (!paginationEl) return;

    const totalPages = Math.ceil(totalCount / this.customProductsPerPage);
    if (totalPages <= 1) {
      paginationEl.innerHTML = '';
      paginationEl.classList.add('hidden');
      return;
    }

    const pageItems = this.getPaginationPages(totalPages, currentPage);
    const pageLinks = pageItems
      .map((item) => {
        if (item === '...') {
          return `<li><span class="pagination__item">...</span></li>`;
        }

        const page = Number(item);
        if (page === currentPage) {
          return `
            <li>
              <a role="link" aria-disabled="true" class="pagination__item pagination__item--current background-2" aria-current="page" aria-label="Page ${page}">${page}</a>
            </li>
          `;
        }

        const pageUrl = this.buildCustomBrowserUrl({ page });
        return `
          <li>
            <a href="${pageUrl}" class="pagination__item" data-custom-pagination-link="true" data-page="${page}" aria-label="Page ${page}">${page}</a>
          </li>
        `;
      })
      .join('');

    const prevDisabled = currentPage <= 1;
    const nextDisabled = currentPage >= totalPages;
    const prevUrl = this.buildCustomBrowserUrl({ page: Math.max(1, currentPage - 1) });
    const nextUrl = this.buildCustomBrowserUrl({ page: Math.min(totalPages, currentPage + 1) });

    paginationEl.classList.remove('hidden');
    paginationEl.innerHTML = `
      <ul class="pagination__list list-unstyled flex items-center flex-wrap justify-center" role="list">
        <li>
          ${
            prevDisabled
              ? '<span class="pagination__item pagination__item--previous pagination__item-arrow" aria-disabled="true">Previous</span>'
              : `<a href="${prevUrl}" class="pagination__item pagination__item--previous pagination__item-arrow motion-reduce" data-custom-pagination-link="true" data-page="${Math.max(1, currentPage - 1)}">Previous</a>`
          }
        </li>
        ${pageLinks}
        <li>
          ${
            nextDisabled
              ? '<span class="pagination__item pagination__item--next pagination__item-arrow" aria-disabled="true">Next</span>'
              : `<a href="${nextUrl}" class="pagination__item pagination__item--next pagination__item-arrow motion-reduce" data-custom-pagination-link="true" data-page="${Math.min(totalPages, currentPage + 1)}">Next</a>`
          }
        </li>
      </ul>
    `;
  }

  getPaginationPages(totalPages, currentPage) {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const pages = [1];
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    if (start > 2) pages.push('...');
    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }
    if (end < totalPages - 1) pages.push('...');

    pages.push(totalPages);
    return pages;
  }

  isSameCustomFilter(a, b) {
    return (
      String(a?.key || '').trim().toLowerCase() === String(b?.key || '').trim().toLowerCase()
      && String(a?.value || '').trim().toLowerCase() === String(b?.value || '').trim().toLowerCase()
    );
  }

  matchesSingleCustomFilter(searchText, handledSearchText, key, value) {
    const tokens = this.getFilterMatchTokens(key, value);
    return tokens.some((token) => {
      if (!token) return false;
      const normalized = String(token).toLowerCase().trim();
      const handled = this.toHandle(normalized);
      return searchText.includes(normalized) || handledSearchText.includes(handled);
    });
  }

  matchesCustomSearchText(searchText, selectedFilters) {
    const normalizedSearchText = String(searchText || '').toLowerCase();
    const handledSearchText = this.toHandle(normalizedSearchText);

    // Apply strict AND behavior for metafield filters: every selected option must match.
    return selectedFilters.every((filterItem) =>
      this.matchesSingleCustomFilter(
        normalizedSearchText,
        handledSearchText,
        filterItem.key,
        filterItem.value
      )
    );
  }

  getFilterMatchTokens(key, value) {
    const normalizedKey = String(key || '').toLowerCase();
    const normalizedValue = String(value || '').toLowerCase().trim();
    const handledValue = this.toHandle(normalizedValue);
    const tokens = [normalizedValue, handledValue];

    if (normalizedKey.includes('compressor-stage')) {
      if (normalizedValue === 'single') {
        tokens.push('single-stage', '1-stage', 'one-stage', 'scroll compressor');
      } else if (normalizedValue === 'two') {
        tokens.push('two-stage', '2-stage');
      } else if (normalizedValue.includes('variable')) {
        tokens.push('variable-speed', 'inverter');
      }
    }

    if (normalizedKey.includes('blower-motor')) {
      if (normalizedValue.includes('multi')) {
        tokens.push('multi-speed', 'variable-speed', '9-speed', 'ecm motor');
      } else if (normalizedValue.includes('single')) {
        tokens.push('single-speed');
      }
    }

    if (normalizedKey.includes('electrical-phases')) {
      if (normalizedValue === 'single') {
        tokens.push('single phase', '1 phase', '1ph');
      } else if (normalizedValue === 'three') {
        tokens.push('three phase', '3 phase', '3ph');
      }
    }

    return [...new Set(tokens)];
  }

  toHandle(value) {
    if (FoxTheme?.utils?.handleize) return FoxTheme.utils.handleize(value);
    return value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  async updateCustomFilterCounts() {
    const customInputs = Array.from(
      this.querySelectorAll('input[type="checkbox"][data-custom-filter-key][data-custom-filter-value]')
    );
    if (!customInputs.length) return;

    try {
      const searchTexts = await this.getCustomSearchTexts();
      const activeCustomFilters = this.getActiveCustomFilters();

      customInputs.forEach((input) => {
        const key = input.dataset.customFilterKey || '';
        const value = input.dataset.customFilterValue || '';
        const currentFilter = { key, value };
        const otherActiveFilters = activeCustomFilters.filter(
          (item) => !this.isSameCustomFilter(item, currentFilter)
        );
        const candidateFilters = [...otherActiveFilters, currentFilter];

        const count = searchTexts.reduce((acc, text) => {
          const matched = this.matchesCustomSearchText(text, candidateFilters);
          return matched ? acc + 1 : acc;
        }, 0);

        const countEl = input.closest('li')?.querySelector('.count');
        if (countEl) {
          countEl.classList.remove('count--loading');
          countEl.textContent = `${count}`;
        }

        // Keep active filters clickable so users can always uncheck them.
        input.disabled = !input.checked && count === 0;
      });
    } catch (error) {
      console.error('Custom filter count build error:', error);
      customInputs.forEach((input) => {
        const countEl = input.closest('li')?.querySelector('.count');
        if (!countEl) return;
        countEl.classList.remove('count--loading');
        if ((countEl.textContent || '').trim() === '...') {
          countEl.textContent = '0';
        }
        if (!input.checked) {
          input.disabled = true;
        }
      });
    }

    this.normalizeCountLoaderState();
  }

  async getCustomSearchTexts() {
    const requestUrl = this.buildCustomApiUrlWithOptions({ includeCustomFilters: false });
    const cachedBaseCards = this.customBaseCardsCache.get(requestUrl);
    if (cachedBaseCards?.length) {
      return cachedBaseCards.map((card) => card.searchText || '');
    }

    const pendingBaseCards = this.customBaseCardsPromiseCache.get(requestUrl);
    if (pendingBaseCards) {
      const cards = await pendingBaseCards;
      return cards.map((card) => card.searchText || '');
    }

    if (this._customSearchTexts?.length && this._customSearchTextsKey === requestUrl) return this._customSearchTexts;
    if (this._customSearchTextsPromise && this._customSearchTextsKey === requestUrl) return this._customSearchTextsPromise;

    this._customSearchTextsPromise = (async () => {
      let pageUrl = requestUrl;
      let allHtml = '';

      while (pageUrl) {
        const response = await fetch(pageUrl);
        const data = await response.json();
        console.log('[custom-filter-products] getCustomSearchTexts response', {
          pageUrl,
          hasHtml: Boolean(data?.html),
          htmlLength: data?.html?.length || 0,
          paginateUrl: data?.paginateUrl || null,
        });
        allHtml += data?.html || '';
        pageUrl = data?.paginateUrl ? new URL(data.paginateUrl, window.location.origin).toString() : null;
      }

      const parser = document.createElement('div');
      parser.innerHTML = allHtml;
      this._customSearchTexts = Array.from(parser.querySelectorAll('.product-column')).map(
        (card) => card.dataset.customSearchText || ''
      );
      this._customSearchTextsKey = requestUrl;
      return this._customSearchTexts;
    })();

    return this._customSearchTextsPromise;
  }

  scheduleCustomFilterCountRefresh() {
    if (this._customFilterCountRefreshTimer) {
      clearTimeout(this._customFilterCountRefreshTimer);
    }
    this._customFilterCountRefreshTimer = setTimeout(() => {
      this.updateCustomFilterCounts();
    }, 120);
  }

  updateCustomCount(totalCount) {
    const productCount = document.getElementById('ProductCount');
    if (!productCount) return;

    const baseRequestUrl = this.buildCustomApiUrlWithOptions({ includeCustomFilters: false });
    const totalProducts = this.customBaseCardsCache.get(baseRequestUrl)?.length || totalCount;
    productCount.innerText = `${totalCount} of ${totalProducts} products`;
  }

  buildUrl() {
    const searchParams = new URLSearchParams(new FormData(this));
    const url = new URL(this.action);

    url.search = '';
    searchParams.forEach((value, key) => url.searchParams.append(key, value));

    ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((item) => {
      if (url.searchParams.get(item) === '') {
        url.searchParams.delete(item);
      }
    });

    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  updateURLHash(url) {
    const clonedUrl = new URL(url);
    clonedUrl.searchParams.delete('section_id');
    history.replaceState({}, '', clonedUrl.toString());
  }

  scrollToCollectionTop() {
    const target =
      document.querySelector('.collection') ||
      document.getElementById('ProductGridContainer') ||
      document.body;

    const top = target.getBoundingClientRect().top + window.scrollY - 120;
    // Only move upward for filter actions; never push user downward.
    if (window.scrollY <= top) return;
    window.scrollTo({
      top: Math.max(0, top),
      behavior: FoxTheme.config.motionReduced ? 'auto' : 'smooth',
    });
  }

  beforeRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const productCount = document.getElementById('ProductCount');
    const isCheckboxInteraction = this.pendingInputType === 'checkbox';
    if (!isCheckboxInteraction) {
      this.renderProductGridSkeleton();
    }
    if (container) {
      container.style.opacity = isCheckboxInteraction ? '0.75' : '1';
      container.style.transform = 'none';
    }
    if (productCount && !isCheckboxInteraction) {
      productCount.textContent = 'Loading products...';
    }

    if (loadings && !isCheckboxInteraction) {
      loadings.forEach((loading) => {
        loading.classList.add('btn--loading');
      });
    }
  }

  afterRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const items = container.querySelectorAll('.product-card');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    if (this.isCustomFiltering) {
      if (container) {
        container.style.opacity = '1';
        container.style.transform = 'none';
      }
      items.forEach((item) => {
        item.style.opacity = '1';
        item.style.visibility = 'visible';
        item.style.transform = 'none';
      });
    } else {
      FoxTheme.Motion.timeline([
        [container, { y: [translateY, 0], opacity: [0, 1] }],
        [
          items,
          { y: [translateY, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] },
          { duration: 0.5, delay: FoxTheme.config.motionReduced ? 0 : FoxTheme.Motion.stagger(0.1) },
        ],
      ]);
    }

    if (loadings) {
      loadings.forEach((loading) => {
        loading.classList.remove('btn--loading');
      });
    }

    this.normalizeCountLoaderState();
    this.isCustomFiltering = false;
    this.pendingFilterIndex = null;
    this.pendingInputType = null;
    if (container) {
      container.style.opacity = '';
    }

    document.dispatchEvent(new CustomEvent('collection:rerendered'));
  }

  normalizeCountLoaderState() {
    document.querySelectorAll('.count.count--loading').forEach((countEl) => {
      const value = (countEl.textContent || '').trim();
      if (value !== '...') {
        countEl.classList.remove('count--loading');
      }
    });
  }

  renderProductGridSkeleton(count = 12) {
    const productsList = document.getElementById('ProductsList');
    if (!productsList) return;

    const safeCount = Math.max(8, Math.min(Number(count) || 12, 30));
    const skeletonItems = Array.from({ length: safeCount })
      .map(
        () => `
          <div class="f-column product-column">
            <div class="product-card product-card-style-card color-scheme-1 color-inherit product-card--skeleton">
              <div class="product-card__wrapper h-full tw-flex tw-flex-col">
                <div class="product-card__image-wrapper color-scheme-1 bg-none">
                  <div class="filter-skeleton filter-skeleton--media"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5">...path content...</svg></div>
                </div>
                <div class="product-card__info tw-flex tw-flex-col tw-flex-1 text-left">
                  <div class="filter-skeleton filter-skeleton--brand"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5">...path content...</svg></div>
                  <div class="filter-skeleton filter-skeleton--title"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5">...path content...</svg></div>
                  <div class="filter-skeleton filter-skeleton--title filter-skeleton--title-short"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5">...path content...</svg></div>
                  <div class="product-card__bottom tw-flex tw-flex-col tw-flex-1 tw-gap-2">
                    <div class="filter-skeleton filter-skeleton--model"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5">...path content...</svg></div>
                    <div class="filter-skeleton filter-skeleton--price"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5">...path content...</svg></div>
                    <div class="filter-skeleton filter-skeleton--button"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5">...path content...</svg></div>
                    <div class="filter-skeleton filter-skeleton--promo"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5">...path content...</svg></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        `
      )
      .join('');

    productsList.innerHTML = skeletonItems;
  }

  renderSection(url, event) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url, event) : this.renderSectionFromFetch(url, event);
    this.updatePaginationVisibility(url);

    if (this.hasAttribute('data-history')) this.updateURLHash(url);

    this.dirty = false;
  }

  updatePaginationVisibility(url, { forceShow = false } = {}) {
    const pagination = document.querySelector('#ProductGridContainer .pagination');
    if (!pagination) return;

    if (forceShow) {
      pagination.classList.remove('hidden');
      return;
    }

    const currentUrl = new URL(url, window.location.origin);
    const hasFilters = Array.from(currentUrl.searchParams.keys()).some((key) => key.startsWith('filter.'));

    if (hasFilters) {
      pagination.classList.add('hidden');
    } else {
      pagination.classList.remove('hidden');
    }
  }

  renderCustomActiveFilters(url) {
    const container = document.getElementById('FacetFiltersActive');
    if (!container) return;

    container.querySelectorAll('[data-custom-active-filter]').forEach((el) => el.remove());
    container.querySelectorAll('[data-custom-clear-all]').forEach((el) => el.remove());

    const currentUrl = new URL(url, window.location.origin);
    const customEntries = Array.from(currentUrl.searchParams.entries()).filter(([key, value]) => {
      return key.startsWith('filter.p.m.custom.') && value !== '';
    });
    if (!customEntries.length) return;

    customEntries.forEach(([key, value]) => {
      const removeUrl = new URL(currentUrl.toString());
      const allValues = removeUrl.searchParams.getAll(key);
      removeUrl.searchParams.delete(key);
      removeUrl.searchParams.delete('section_id');
      removeUrl.searchParams.delete('page');

      let removed = false;
      allValues.forEach((existingValue) => {
        if (!removed && existingValue === value) {
          removed = true;
          return;
        }
        removeUrl.searchParams.append(key, existingValue);
      });

      const chipHref = `${removeUrl.pathname}${removeUrl.search}`;
      const hasExisting = Array.from(container.querySelectorAll('.facet-remove')).some((link) => {
        const title = (link.querySelector('.facet-title')?.textContent || '').trim().toLowerCase();
        const normalizedValue = String(value || '').trim().toLowerCase();
        return title === normalizedValue;
      });
      if (hasExisting) return;

      const chip = this.createFacetRemoveLink();
      chip.className = 'facet-remove btn btn--secondary flex relative';
      chip.setAttribute('data-custom-active-filter', 'true');
      chip.dataset.filterParam = key;
      chip.dataset.filterValue = value;
      chip.href = chipHref;
      chip.setAttribute('aria-label', `Clear ${value}`);
      chip.innerHTML = `
        <div class="btn__text flex items-center justify-between gap-2">
          <div class="facet-title text-wrap flex items-center gap-2">${value}</div>
          <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-close icon--extra-small" fill="none" viewBox="0 0 24 24">
            <path d="M18.75 5.13496L5.25 18.8544" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
            <path d="M18.75 18.8544L5.25 5.13496" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
          </svg>
        </div>
      `;
      const clearAllLink = container.querySelector('.facet-clear-all');
      if (clearAllLink) {
        container.insertBefore(chip, clearAllLink);
      } else {
        container.appendChild(chip);
      }
    });

    const hasClearAll = container.querySelector('.facet-clear-all');
    if (!hasClearAll) {
      const clearUrl = new URL(currentUrl.toString());
      Array.from(clearUrl.searchParams.keys()).forEach((paramKey) => {
        if (paramKey.startsWith('filter.') || paramKey === 'page' || paramKey === 'section_id') {
          clearUrl.searchParams.delete(paramKey);
        }
      });

      const clearAll = this.createFacetRemoveLink();
      clearAll.className = 'facet-clear-all btn--link';
      clearAll.setAttribute('data-custom-clear-all', 'true');
      clearAll.href = `${clearUrl.pathname}${clearUrl.search}`;
      clearAll.textContent = 'Clear all';
      container.appendChild(clearAll);
    }

    const clearAllLink = container.querySelector('.facet-clear-all');
    if (clearAllLink) {
      container.appendChild(clearAllLink);
    }
  }

  createFacetRemoveLink() {
    let link;
    try {
      link = document.createElement('a', { is: 'facet-remove' });
    } catch (error) {
      link = document.createElement('a');
    }

    link.setAttribute('is', 'facet-remove');

    if (typeof FacetRemove !== 'undefined' && !(link instanceof FacetRemove)) {
      link.addEventListener('click', (event) => {
        FacetRemove.prototype.onClick.call(link, event);
      });
    }

    return link;
  }

  renderSectionFromFetch(url, event) {
    this.beforeRenderSection();
    const start = performance.now();

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const execution = performance.now() - start;

        setTimeout(
          () => {
            this.renderFilters(responseText, event);
            this.renderFiltersActive(responseText);
            this.renderProductGridContainer(responseText);
            this.renderFacetFiltersContainer(responseText);
            this.renderProductCount(responseText);
            this.renderSortBy(responseText);
            this.renderSortByMobile(responseText);
            this.scheduleCustomFilterCountRefresh();

            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });
            this.cachedMap.set(url, responseText);

            this.afterRenderSection();
          },
          execution > 250 ? 0 : 250
        );
      });
  }

  renderSectionFromCache(url, event) {
    this.beforeRenderSection();

    setTimeout(() => {
      const responseText = this.cachedMap.get(url);
      this.renderFilters(responseText, event);
      this.renderFiltersActive(responseText);
      this.renderProductGridContainer(responseText);
      this.renderFacetFiltersContainer(responseText);
      this.renderProductCount(responseText);
      this.renderSortBy(responseText);
      this.renderSortByMobile(responseText);
      this.scheduleCustomFilterCountRefresh();

      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });

      this.afterRenderSection();
    }, 250);
  }

  renderFilters(responseText, event) {
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const facetElements = parsedHTML.querySelectorAll(
      '#FacetFiltersContainer [data-filter], #MobileFacetFiltersContainer [data-filter], #DrawerFacetFiltersContainer [data-filter]'
    );

    const facetElementsFormDom = document.querySelectorAll(
      '#FacetFiltersContainer [data-filter], #MobileFacetFiltersContainer [data-filter], #DrawerFacetFiltersContainer [data-filter]'
    );
    const openStateById = new Map();
    Array.from(facetElementsFormDom).forEach((currentElement) => {
      if (currentElement.tagName === 'DETAILS' && currentElement.id) {
        openStateById.set(currentElement.id, currentElement.hasAttribute('open'));
      }
    });

    Array.from(facetElementsFormDom).forEach((currentElement) => {
      if (!Array.from(facetElements).some(({ id }) => currentElement.id === id)) {
        currentElement.classList.add('hidden');
      }
    });

    const matchesIndex = (element) => {
      const eventFilterIndex = event?.target?.closest?.('[data-filter]')?.dataset?.index;
      const activeFilterIndex = this.pendingFilterIndex || eventFilterIndex;
      return activeFilterIndex ? element.dataset.index === activeFilterIndex : false;
    };
    const facetsToRender = Array.from(facetElements).filter((element) => !matchesIndex(element));

    facetsToRender.forEach((element) => {
      const filter = (element.id && document.getElementById(element.id))
        || document.querySelector(`[data-filter][data-index="${element.dataset.index}"]`);
      if (filter !== null) {
        filter.classList.remove('hidden');
        if (filter.tagName === 'DETAILS') {
          filter.querySelector('summary + *').innerHTML = element.querySelector('summary + *').innerHTML;
          if (filter.id && openStateById.has(filter.id)) {
            if (openStateById.get(filter.id)) {
              filter.setAttribute('open', '');
            } else {
              filter.removeAttribute('open');
            }
          }
        } else {
          filter.innerHTML = element.innerHTML;
        }
      }
    });
  }

  renderFiltersActive(responseText) {
    const id = 'FacetFiltersActive';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderProductGridContainer(responseText) {
    const id = 'ProductGridContainer';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;

    const layoutSwitcher = document.querySelector('layout-switcher');
    if (layoutSwitcher) layoutSwitcher.onButtonClick(layoutSwitcher.querySelector('.btn--active'));
  }

  renderFacetFiltersContainer(responseText) {
    const id = 'FacetFiltersContainer';
    if (document.getElementById(id) === null) return;
    const currentContainer = document.getElementById(id);
    const openFiltersById = new Set(
      Array.from(currentContainer.querySelectorAll('details[data-filter][open][id]')).map((el) => el.id)
    );
    const openFiltersByIndex = new Set(
      Array.from(currentContainer.querySelectorAll('details[data-filter][open][data-index]')).map((el) => el.dataset.index)
    );
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    currentContainer.innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;

    currentContainer.querySelectorAll('details[data-filter]').forEach((detailEl) => {
      const shouldOpenById = detailEl.id && openFiltersById.has(detailEl.id);
      const shouldOpenByIndex = detailEl.dataset.index && openFiltersByIndex.has(detailEl.dataset.index);
      if (shouldOpenById || shouldOpenByIndex) {
        detailEl.setAttribute('open', '');
      }
    });
  }

  renderProductCount(responseText) {
    const id = 'ProductCount';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderSortBy(responseText) {
    const id = 'SortByContainer';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderSortByMobile(responseText) {
    const id = 'SortByContainerMobile';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }
}
customElements.define('facet-form', FacetForm, { extends: 'form' });

class FacetRemove extends HTMLAnchorElement {
  constructor() {
    super();
    this.addEventListener('click', this.onClick);
  }

  static removeSingleParamValue(url, param, value) {
    const targetUrl = new URL(url.toString());
    const allValues = targetUrl.searchParams.getAll(param);
    targetUrl.searchParams.delete(param);

    let removed = false;
    allValues.forEach((existingValue) => {
      if (!removed && existingValue === value) {
        removed = true;
        return;
      }
      targetUrl.searchParams.append(param, existingValue);
    });

    return targetUrl;
  }

  onClick(event) {
    const form = this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');

    if (form) {
      event.preventDefault();

      const isClearAll = this.classList.contains('facet-clear-all');
      const customParam = this.dataset.filterParam || '';
      const customValue = this.dataset.filterValue || '';
      const hasCustomChipFlag = this.hasAttribute('data-custom-active-filter');
      const customUrlFromChip = new URL(window.location.href);
      const url = isClearAll ? this.getClearAllUrl() : new URL(this.href, window.location.origin);

      let customTargetUrl = url;
      if (customParam && customValue) {
        customTargetUrl = FacetRemove.removeSingleParamValue(customUrlFromChip, customParam, customValue);
      }
      customTargetUrl.searchParams.delete('section_id');
      customTargetUrl.searchParams.delete('page');

      const isCustomAction =
        hasCustomChipFlag
        || (isClearAll && typeof form.hasActiveCustomFilters === 'function' && form.hasActiveCustomFilters())
        || (customParam && customValue);

      if (
        isCustomAction
        && typeof form.applyCustomFiltersFromUrl === 'function'
        && typeof form.renderCustomFilteredProducts === 'function'
      ) {
        form.applyCustomFiltersFromUrl(customTargetUrl);
        form.setCustomPageInput(1);
        form.dirty = true;

        const browserUrl = form.buildCustomBrowserUrl({ page: 1 });
        form.updateURLHash(browserUrl);
        form.renderCustomActiveFilters(browserUrl);
        form.renderCustomFilteredProducts(event);
        return;
      }

      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      form.renderSection(url.toString(), event);
    }
  }

  getClearAllUrl() {
    const current = new URL(window.location.href);
    const fallback = new URL(this.href, window.location.origin);
    const url = new URL(current.pathname || fallback.pathname, window.location.origin);

    // Keep non-filter params (search/sort), remove all filter and pagination params.
    const sourceParams = current.searchParams.toString() ? current.searchParams : fallback.searchParams;
    sourceParams.forEach((value, key) => {
      if (key.startsWith('filter.') || key === 'page' || key === 'section_id' || value === '') return;
      url.searchParams.append(key, value);
    });

    return url;
  }
}
customElements.define('facet-remove', FacetRemove, { extends: 'a' });

// Fallback for browsers where customized built-in elements are not upgraded reliably.
document.addEventListener('click', (event) => {
  const link = event.target.closest('a[is="facet-remove"]');
  if (!link) return;
  if (typeof FacetRemove === 'undefined') return;
  if (link instanceof FacetRemove) return;

  FacetRemove.prototype.onClick.call(link, event);
});

class FacetCount extends HTMLElement {
  constructor() {
    super();
  }

  facetUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.facetUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate,
      this.onFacetUpdate.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.facetUpdateUnsubscriber) {
      this.facetUpdateUnsubscriber();
    }
  }

  get itemCount() {
    return parseInt(this.innerText.replace(/\D/g, ''));
  }

  onFacetUpdate(event) {
    const parsedHTML = new DOMParser().parseFromString(event.responseText, 'text/html');
    const facetCount = parsedHTML.querySelector('facet-count');
    this.innerText = facetCount && facetCount.innerHTML;
    this.hidden = this.itemCount === 0;
  }
}
customElements.define('facet-count', FacetCount);

class FacetToggler extends HTMLButtonElement {
  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
  }

  connectedCallback() {
    this.facetEl = document.getElementById('FacetFiltersContainer');
    this.addEventListener('click', this.onClickHandler);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
  }

  onClick(evt) {
    evt.preventDefault();

    if (this.facetEl.classList.contains('xl:block')) {
      this.facetEl.classList.remove('xl:block');
    } else {
      this.facetEl.classList.add('xl:block');
    }
  }
}
customElements.define('facet-toggler', FacetToggler, { extends: 'button' });

class LoadMoreButton extends HTMLButtonElement {
  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClickHandler);

    if (this.getAttribute('type') == 'infinite') {
      FoxTheme.Motion.inView(this, this.onClickHandler, { margin: '200px 0px 200px 0px' });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
  }

  onClick() {
    if (this.classList.contains('btn--loading')) return;
    this.loadingState();

    const url = this.setUrl().toString();

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        this.renderPagination(responseText);
        this.renderProductGridContainer(responseText);
      });
  }

  renderPagination(responseText) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const pagination = productGridContainer.querySelector('.pagination');
    const source = parsedHTML.querySelector('.pagination');

    if (source) {
      pagination.innerHTML = source.innerHTML;
    } else {
      pagination.remove();
    }
  }

  renderProductGridContainer(responseText) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const productList = productGridContainer.querySelector('.products-list');
    const source = parsedHTML.querySelector('.products-list');

    if (source && productList) {
      source.querySelectorAll('.f-column').forEach((item) => {
        productList.appendChild(item);
      });
    }
  }

  setUrl() {
    const url = new URL(this.getAttribute('action'));
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  loadingState() {
    this.classList.add('pointer-events-none');
    this.classList.add('btn--loading');
  }
}
customElements.define('load-more-button', LoadMoreButton, { extends: 'button' });

class LayoutSwitcher extends HTMLElement {
  constructor() {
    super();
    this.cookieName = 'hypertheme:collection-layout';

    this.initLayoutMode();
    this.buttons.forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });
  }

  get buttons() {
    return this.querySelectorAll('button');
  }

  /**
   * @param {event|element} evt
   */
  onButtonClick(evt) {
    const target = evt.currentTarget ? evt.currentTarget : evt;
    this.changeLayout(target, target.dataset.layoutMode);
  }

  initLayoutMode() {
    if (FoxTheme.config.hasLocalStorage) {
      const layoutMode = window.localStorage.getItem(this.cookieName);

      if (layoutMode !== null) {
        const target = this.querySelector(`button[data-layout-mode="${layoutMode}"]`);
        if (target) {
          this.changeLayout(target, layoutMode);
        }
      }
    }
  }

  changeLayout(target, layoutMode) {
    const productGrid = document.getElementById('ProductsList');
    if (!productGrid) return;

    productGrid.dataset.layout = layoutMode;

    const { gridClass, listClass } = productGrid.dataset;
    if ((gridClass || listClass) && gridClass !== listClass) {
      const productCards = productGrid.querySelectorAll('.product-card');
      productCards &&
        productCards.forEach((card) => {
          if ('grid' === layoutMode) {
            listClass && card.classList.remove(listClass);
            gridClass && card.classList.add(gridClass);
          } else {
            gridClass && card.classList.remove(gridClass);
            listClass && card.classList.add(listClass);
          }
        });
    }

    this.buttons.forEach((button) => {
      button.classList.remove('btn--active');
    });
    target.classList.add('btn--active');

    if (FoxTheme.config.hasLocalStorage) {
      window.localStorage.setItem(this.cookieName, layoutMode);
    }
  }
}
customElements.define('layout-switcher', LayoutSwitcher);
